import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useAuthToken from './useAuthToken';
import useAuthUser from './useAuthUser';

const useAuth = () => {
    const navigate = useNavigate();
    const { token, saveToken, removeToken, isTokenValid } = useAuthToken();
    const { 
        user, 
        isLoading: isUserLoading, 
        error: userError,
        updateUserFromToken, 
        fetchUserData,
        hasSelectedStarter 
    } = useAuthUser(token);

    const updateUserData = useCallback(async () => {
        if (token) {
            try {
                const userData = await fetchUserData(token);
                return userData;
            } catch (error) {
                console.error('Erreur lors de la mise à jour des données utilisateur:', error);
                return null;
            }
        }
        return null;
    }, [token, fetchUserData]);

    const login = useCallback(async (username, password) => {
        try {
            const response = await axios.post('https://api.poketask.fr/api/login', {
                username,
                password
            });

            const newToken = response.data.token;
            saveToken(newToken);
            await updateUserFromToken(newToken);
            const userData = await fetchUserData(newToken);
            
            if (!userData.hasSelectedStarter) {
                navigate('/starter_selection');
            } else if (userData.roles?.includes('ROLE_ADMIN')) {
                navigate('/accueiladmin');
            } else {
                navigate('/dashboard');
            }

            return { success: true };
        } catch (error) {
            let errorMessage = 'Erreur de connexion';
            
            if (error.response?.status === 401) {
                errorMessage = 'Identifiants incorrects';
            } else if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            }
            
            return { 
                success: false, 
                error: errorMessage
            };
        }
    }, [navigate, saveToken, updateUserFromToken, fetchUserData]);

    const logout = useCallback(() => {
        removeToken();
        navigate('/');
    }, [navigate, removeToken]);

    return {
        user,
        token,
        login,
        logout,
        updateUserData, 
        isLoading: isUserLoading,
        error: userError,
        isAuthenticated: !!token && !!user && isTokenValid(),
        hasSelectedStarter
    };
};

export default useAuth;